import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { unifiedFilterStore } from "../../store/unifiedFilters";
import type { PartialUnifiedfilterState } from "../../utils/filters.utils";
import { encodeUnifiedFilterStatePayload } from "../../utils/filters.utils";
import { QueryParam } from "../../utils/permission.utils";

/**
 * Hook to manage encoding and updating the unified filter state in the URL
 * This allows filter selections to be shared via URL
 */
const useEncodedUnifiedFilterStateUrl = (): [
    (payload?: PartialUnifiedfilterState) => void,
    () => string,
] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsRef = useRef<URLSearchParams>(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const setEncodedUnifiedFilterStateUrl = useCallback(
        (payload?: PartialUnifiedfilterState) => {
            const { searchQuery, filters } = unifiedFilterStore.getState();

            // If we were given a payload, then use that, otherwise grab the current state
            const payloadToEncode: PartialUnifiedfilterState = payload ?? {
                filters,
                searchQuery,
            };

            // If we were given a payload, then set the filter state
            if (payload) {
                const currentState = unifiedFilterStore.getState();
                const hasChanges =
                    JSON.stringify(payload.filters) !==
                        JSON.stringify(currentState.filters) ||
                    payload.searchQuery !== currentState.searchQuery;

                if (hasChanges) {
                    unifiedFilterStore.setState(payload);
                }
            }

            const encoded = encodeUnifiedFilterStatePayload(
                payloadToEncode,
                true,
            );
            if (encoded) {
                const newSearchParams = new URLSearchParams(
                    searchParamsRef.current,
                );
                newSearchParams.set(QueryParam.UNIFIED_FILTERS, encoded);
                setSearchParams(newSearchParams);
            }
        },
        [searchParams, setSearchParams],
    );

    const getEncodedUnifiedFilterStateUrl = () => {
        return window.location.href;
    };

    return [setEncodedUnifiedFilterStateUrl, getEncodedUnifiedFilterStateUrl];
};

export default useEncodedUnifiedFilterStateUrl;
